'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["ownerState", "children"],
  _excluded2 = ["slots", "slotProps", "className", "sx", "classes"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { MessageMeta } from '@mui/x-chat-headless';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { useChatMessageUtilityClasses } from "./chatMessageClasses.mjs";
import { mergeSlotProps } from "../internals/mergeSlotProps.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatMessageMeta');
const ChatMessageMetaStyled = styled('div', {
  name: 'MuiChatMessage',
  slot: 'Meta',
  overridesResolver: (_, styles) => styles.meta
})(({
  theme,
  ownerState
}) => _extends({
  gridArea: 'meta',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontSize: theme.typography.caption.fontSize,
  color: (theme.vars || theme).palette.text.disabled,
  lineHeight: 1.4,
  minHeight: '1.2em'
}, (ownerState?.variant === 'compact' || ownerState?.isOwnMessage) && {
  justifyContent: 'flex-end'
}, ownerState?.variant === 'compact' && {
  alignSelf: 'start',
  whiteSpace: 'nowrap'
}));
const ChatMessageStatusStyled = styled('span', {
  name: 'MuiChatMessage',
  slot: 'Status',
  overridesResolver: (_, styles) => styles.status
})(({
  ownerState
}) => _extends({
  display: 'inline-flex',
  alignItems: 'center'
}, ownerState?.variant === 'compact' && {
  order: -1
}));

/**
 * Custom Status slot for ChatMessage.
 * In compact mode, renders a Done icon for "sent" status and DoneAll icon
 * for "read" status instead of text labels.
 */
const ChatMessageStatusSlot = /*#__PURE__*/React.forwardRef(function ChatMessageStatusSlot(_ref, ref) {
  let {
      ownerState,
      children
    } = _ref,
    other = _objectWithoutPropertiesLoose(_ref, _excluded);
  const isCompact = ownerState?.variant === 'compact';
  const status = ownerState?.message?.status;
  const isSent = status === 'sent';
  const isRead = status === 'read';
  let content = children;
  if (isCompact && isSent) {
    content = /*#__PURE__*/_jsx(DoneIcon, {
      sx: {
        fontSize: '1em'
      },
      "aria-hidden": "true"
    });
  } else if (isCompact && isRead) {
    content = /*#__PURE__*/_jsx(DoneAllIcon, {
      sx: {
        fontSize: '1em'
      },
      "aria-hidden": "true"
    });
  }
  return /*#__PURE__*/_jsx(ChatMessageStatusStyled, _extends({
    ref: ref,
    ownerState: ownerState
  }, other, {
    children: content
  }));
});
if (process.env.NODE_ENV !== "production") ChatMessageStatusSlot.displayName = "ChatMessageStatusSlot";
const ChatMessageMeta = /*#__PURE__*/React.forwardRef(function ChatMessageMeta(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatMessageMeta'
  });
  // Drop a JS/theme-injected `classes` (not a prop on this sub-part — it shares
  // the `MuiChatMessage-*` namespace) so it can't leak onto the DOM via `...other`.
  const _ref2 = props,
    {
      slots,
      slotProps,
      className,
      sx,
      classes: classesProp
    } = _ref2,
    other = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  void classesProp;
  const classes = useChatMessageUtilityClasses(undefined);
  return /*#__PURE__*/_jsx(MessageMeta, _extends({
    ref: ref
  }, other, {
    slots: _extends({
      meta: ChatMessageMetaStyled,
      status: ChatMessageStatusSlot
    }, slots),
    slotProps: _extends({}, slotProps, {
      meta: mergeSlotProps({
        className: clsx(classes.meta, className),
        sx
      }, slotProps?.meta)
    })
  }));
});
if (process.env.NODE_ENV !== "production") ChatMessageMeta.displayName = "ChatMessageMeta";
process.env.NODE_ENV !== "production" ? ChatMessageMeta.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  className: PropTypes.string,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object])
} : void 0;
export { ChatMessageMeta };